import { SupplierSelectionPriceState } from '../../create-enquiry/components/SupplierSelection.types';
import { useContractPackagePrices } from '../../create-enquiry/hooks/useContractPackagePrices';
import { useProductsByIds } from '../../create-enquiry/hooks/useProductsByIds';
import { UseSupplierSelectionPricesProps } from '../../create-enquiry/hooks/useSupplierSelectionPrices.types';
import { buildContractSupplierSelectionPriceRequests } from '../../create-enquiry/util/buildContractSupplierSelectionPriceRequests';
import { deriveSupplierSelectionPriceInputs } from '../../create-enquiry/util/deriveSupplierSelectionPriceInputs';
import { mapContractSupplierSelectionPrices } from '../../create-enquiry/util/mapContractSupplierSelectionPrices';

export const useSupplierSelectionPrices = ({
    suppliers,
    items,
    portId,
    customerId,
    vesselId,
    deliveryDate,
    contractIdsBySupplierId = {},
    selectedContractIdsBySupplierId = {},
}: UseSupplierSelectionPricesProps) => {
    const { translatedItemsBySupplier, productIds, shouldFetchPrices } = deriveSupplierSelectionPriceInputs({
        suppliers,
        items,
        portId,
        customerId,
    });

    const contractPriceRequests = buildContractSupplierSelectionPriceRequests({
        customerId,
        vesselId,
        portId,
        translatedItemsBySupplier,
        contractIdsBySupplierId,
    });
    const shouldFetchContractPrices = shouldFetchPrices && contractPriceRequests.length > 0;
    const productsQuery = useProductsByIds(productIds);
    const contractPackagePricesQuery = useContractPackagePrices(
        contractPriceRequests,
        deliveryDate,
        shouldFetchContractPrices
    );

    const contractPriceTargets = suppliers.flatMap((supplier) => {
        return (contractIdsBySupplierId[supplier.id] ?? []).map((contractId) => ({
            contractId,
            supplierId: supplier.id,
        }));
    });
    const pricesByContractId = mapContractSupplierSelectionPrices({
        targets: contractPriceTargets,
        translatedItemsBySupplier,
        shouldFetchPrices: shouldFetchContractPrices,
        products: productsQuery.data || [],
        isProductsPending: productsQuery.isPending,
        isProductsError: productsQuery.isError,
        contractPrices: contractPackagePricesQuery.data || [],
        isContractPricesPending: contractPackagePricesQuery.isPending,
        isContractPricesError: contractPackagePricesQuery.isError,
    });
    const contractPricesBySupplierId = Object.fromEntries(
        suppliers.map((supplier) => {
            const selectedContractId = selectedContractIdsBySupplierId[supplier.id];
            return [
                supplier.id,
                selectedContractId
                    ? pricesByContractId[selectedContractId] ?? { state: SupplierSelectionPriceState.HIDDEN }
                    : { state: SupplierSelectionPriceState.HIDDEN },
            ];
        })
    );

    return {
        pricesBySupplierId: contractPricesBySupplierId,
        pricesByContractId,
    };
};
